import React, { useState, useEffect, useCallback } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { apiCall } from "../config/api";
import { visuallyHidden } from "@mui/utils";
import { useAuth } from "../contexts/AuthContext";

const formatDate = (timestamp) => {
  if (!timestamp) {
    return "Tarih Yok";
  }

  // Handle Date objects (from backend API)
  if (timestamp instanceof Date) {
    return timestamp.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // Handle Firestore Timestamp objects
  if (typeof timestamp.toDate === "function") {
    return timestamp.toDate().toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // Handle timestamp strings
  if (typeof timestamp === "string") {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Geçersiz Tarih";
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  // Fallback for older data or different formats
  if (timestamp._seconds) {
    return new Date(timestamp._seconds * 1000).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  return "Tarih Yok";
};

function descendingComparator(a, b, orderBy) {
  let valA = a[orderBy];
  let valB = b[orderBy];

  if (orderBy === "createdAt" && a.createdAt && b.createdAt) {
    valA = a.createdAt.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
    valB = b.createdAt.toDate ? b.createdAt.toDate() : new Date(b.createdAt);

    // Invalid Date kontrolü
    if (isNaN(valA.getTime())) valA = null;
    if (isNaN(valB.getTime())) valB = null;

    // Null handling (put nulls at the end)
    if (!valA && !valB) return 0;
    if (!valA) return 1;
    if (!valB) return -1;
  }

  if (valB < valA) return -1;
  if (valB > valA) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  { id: "title", numeric: false, label: "Pin Başlığı" },
  { id: "reportCount", numeric: true, label: "Şikayet Sayısı" },
  {
    id: "reportReasons",
    numeric: false,
    label: "Şikayet Nedenleri",
    sortable: false,
  },
  { id: "pinOwnerName", numeric: false, label: "Pin Sahibi" },
  { id: "status", numeric: false, label: "Mevcut Durum" },
  { id: "createdAt", numeric: false, label: "Oluşturulma Tarihi" },
  { id: "actions", numeric: false, label: "Eylemler", sortable: false },
];

const ReportedPinsPage = () => {
  const { currentUser, loading: authLoading } = useAuth();
  const [reportedPins, setReportedPins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("reportCount");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedPinDetails, setSelectedPinDetails] = useState(null);

  const fetchReportedPins = useCallback(async () => {
    // Wait for currentUser only (authLoading can be false but user still loading)
    if (!currentUser) {
      console.log("⏳ Waiting for currentUser...");
      return;
    }

    setLoading(true);
    try {
      console.log("📡 Fetching reported pins for user:", currentUser.email);
      const response = await apiCall("/api/admin/pins/reported");
      if (response.success && response.reports) {
        // Transform backend response to match frontend expectations
        const transformedPins = response.reports.map((report) => ({
          id: report.pinId,
          title: report.pin.name || "İsimsiz Pin",
          reportCount: report.reportCount,
          reportReasons: report.reports.reduce((acc, r) => {
            acc[r.reason] = (acc[r.reason] || 0) + 1;
            return acc;
          }, {}),
          pinOwnerName: report.pin.owner?.displayName || "Bilinmeyen Kullanıcı",
          pinOwnerPhoto: report.pin.owner?.photoURL || null,
          isPubliclyVisible: report.isPubliclyVisible,
          createdAt: report.pin.createdAt,
          detailedReports: report.reports.map((r) => ({
            reporterName: r.reporter?.displayName || "Bilinmeyen Kullanıcı",
            reporterPhoto: r.reporter?.photoURL || null,
            reason: r.reason,
            reportedAt: r.reportedAt,
          })),
        }));
        setReportedPins(transformedPins);
      } else {
        throw new Error(response.error || "Şikayet edilen pinler alınamadı.");
      }
    } catch (err) {
      setError(err.message);
      setSnackbar({
        open: true,
        message: `Hata: ${err.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    // Only fetch when auth is ready
    if (!authLoading && currentUser) {
      fetchReportedPins();
    }
  }, [authLoading, currentUser, fetchReportedPins]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleAction = async (pinId, action) => {
    const actionMessages = {
      approve: "Pin onaylandı ve şikayetler temizlendi.",
      quarantine: "Pin karantinaya alındı.",
      delete: "Pin kalıcı olarak silindi.",
    };

    try {
      const response = await apiCall(`/api/admin/pins/${pinId}/action`, {
        method: "POST",
        body: JSON.stringify({ action }),
      });

      if (response.success) {
        setSnackbar({
          open: true,
          message: actionMessages[action] || "İşlem başarılı!",
          severity: "success",
        });
        // Listeyi yenilemek için pin'i state'den çıkar
        setReportedPins((prevPins) => prevPins.filter((p) => p.id !== pinId));
      } else {
        throw new Error(response.error || "İşlem sırasında bir hata oluştu.");
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: `Hata: ${err.message}`,
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleOpenDetailsModal = (pin) => {
    setSelectedPinDetails(pin);
    setDetailsModalOpen(true);
  };

  const handleCloseDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedPinDetails(null);
  };

  const renderReasonSummary = (reasons) => {
    if (!reasons || Object.keys(reasons).length === 0) return "N/A";
    return Object.entries(reasons)
      .map(([reason, count]) => `${count}x ${reason}`)
      .join(", ");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Şikayet Edilen Pinler
      </Typography>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable !== false ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                        {orderBy === headCell.id ? (
                          <Box component="span" sx={visuallyHidden}>
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {stableSort(reportedPins, getComparator(order, orderBy)).map(
                (pin) => (
                  <TableRow key={pin.id}>
                    <TableCell>{pin.title}</TableCell>
                    <TableCell align="center">{pin.reportCount}</TableCell>
                    <TableCell>
                      {renderReasonSummary(pin.reportReasons)}
                    </TableCell>
                    <TableCell>{pin.pinOwnerName}</TableCell>
                    <TableCell>
                      <Chip
                        label={
                          pin.isPubliclyVisible === false
                            ? "Karantinada"
                            : "Aktif"
                        }
                        color={
                          pin.isPubliclyVisible === false
                            ? "warning"
                            : "success"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(pin.createdAt)}</TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => handleAction(pin.id, "approve")}
                      >
                        a) Onayla
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        onClick={() => handleAction(pin.id, "quarantine")}
                      >
                        b) Karantinaya Al
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleAction(pin.id, "delete")}
                      >
                        c) Sil
                      </Button>
                      <Button
                        size="small"
                        onClick={() => handleOpenDetailsModal(pin)}
                      >
                        Detaylar
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Details Modal */}
      <Dialog
        open={detailsModalOpen}
        onClose={handleCloseDetailsModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Şikayet Detayları: {selectedPinDetails?.title}
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Şikayet Eden</TableCell>
                  <TableCell>Neden</TableCell>
                  <TableCell>Tarih</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedPinDetails?.detailedReports?.map((report, index) => (
                  <TableRow key={index}>
                    <TableCell>{report.reporterName}</TableCell>
                    <TableCell>{report.reason}</TableCell>
                    <TableCell>{formatDate(report.reportedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailsModal}>Kapat</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportedPinsPage;
